import React, { memo, useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation } from "@apollo/client";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  H4,
  H5,
  Small,
  Button,
  Badge,
  Loading,
  Notification,
  Select,
} from "orcs-design-system";

import {
  GetWorkflowRuns,
  GetWorkflowRun,
  TriggerWorkflow,
  CancelWorkflowRun,
  ApproveWorkflowStep,
  RejectWorkflowStep,
  CompleteFileUpload,
} from "src/queries/workflows.graphql";
import { upload } from "src/services/storage";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useApolloSettings } from "src/contexts/global/ApolloSettingsContext";

const PanelContainer = styled.div`
  background: ${themeGet("colors.white")};
  border-radius: ${themeGet("radii.2")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${themeGet("space.r")};
  border-bottom: 1px solid ${themeGet("colors.greyLighter")};
`;

const PanelContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const RunItem = styled.div`
  padding: ${themeGet("space.r")};
  border-bottom: 1px solid ${themeGet("colors.greyLighter")};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${themeGet("colors.greyLightest")};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const RunHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${themeGet("space.xs")};
`;

const RunMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${themeGet("space.s")};
  font-size: 12px;
  color: ${themeGet("colors.grey")};
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RunIdText = styled.code`
  font-size: 10px;
  color: ${themeGet("colors.grey")};
  background: ${themeGet("colors.greyLighter")};
  padding: 2px 6px;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${themeGet("colors.greyLight")};
    color: ${themeGet("colors.greyDarker")};
  }

  &:active {
    background: ${themeGet("colors.primary")};
    color: white;
  }
`;

const StatusBadge = styled(Badge)`
  text-transform: capitalize;
`;

const StepList = styled.div`
  margin-top: ${themeGet("space.s")};
  padding: ${themeGet("space.s")};
  background: ${themeGet("colors.greyLightest")};
  border-radius: ${themeGet("radii.1")};
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  border-bottom: 1px solid ${themeGet("colors.greyLighter")};

  &:last-child {
    border-bottom: none;
  }
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const StepInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const StepDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const StepName = styled.div`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StepStatusText = styled.span`
  font-size: 10px;
  color: ${(props) => {
    switch (props.$status?.toLowerCase()) {
      case "completed":
        return "#28a745";
      case "failed":
        return "#dc3545";
      case "running":
        return "#007bff";
      default:
        return "#6c757d";
    }
  }};
  text-transform: uppercase;
  font-weight: 600;
`;

const StepError = styled.div`
  margin-top: 6px;
  padding: 8px;
  background: #fff5f5;
  border-left: 3px solid #dc3545;
  border-radius: 4px;
  font-size: 11px;
  color: #dc3545;
  word-break: break-word;
`;

const StepSummary = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 11px;
`;

const SummaryItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${(props) => props.$color || themeGet("colors.grey")};
`;

const StepIcon = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: white;
  background: ${(props) => {
    switch (props.$status?.toLowerCase()) {
      case "completed":
        return "#4CAF50";
      case "running":
        return "#2196F3";
      case "failed":
        return "#F44336";
      case "paused":
      case "paused_for_approval":
      case "waiting_approval":
        return "#FF9800";
      case "waiting_upload":
      case "paused_for_upload":
        return "#9C27B0";
      default:
        return "#90A4AE";
    }
  }};
`;

const ApprovalActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ApprovalButton = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  background: ${(props) =>
    props.$variant === "approve"
      ? themeGet("colors.successLighter")
      : themeGet("colors.dangerLighter")};
  color: ${(props) =>
    props.$variant === "approve"
      ? themeGet("colors.success")
      : themeGet("colors.danger")};

  &:hover {
    background: ${(props) =>
      props.$variant === "approve"
        ? themeGet("colors.successLight")
        : themeGet("colors.dangerLight")};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${themeGet("space.r")};
  color: ${themeGet("colors.grey")};
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${themeGet("space.s")};
  padding: ${themeGet("space.s")} ${themeGet("space.r")};
  border-bottom: 1px solid ${themeGet("colors.greyLighter")};
  background: ${themeGet("colors.greyLightest")};
`;

const FilterLabel = styled.span`
  font-size: 12px;
  color: ${themeGet("colors.greyDark")};
  font-weight: 500;
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${themeGet("space.s")} ${themeGet("space.r")};
  border-top: 1px solid ${themeGet("colors.greyLighter")};
  background: ${themeGet("colors.greyLightest")};
`;

const PaginationInfo = styled.span`
  font-size: 12px;
  color: ${themeGet("colors.grey")};
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${themeGet("space.xs")};
`;

const PageButton = styled.button`
  padding: 4px 8px;
  border: 1px solid ${themeGet("colors.greyLight")};
  border-radius: 4px;
  background: ${(props) =>
    props.$active ? themeGet("colors.primary") : "white"};
  color: ${(props) => (props.$active ? "white" : themeGet("colors.greyDark"))};
  font-size: 12px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.$active
        ? themeGet("colors.primaryDark")
        : themeGet("colors.greyLighter")};
  }
`;

const StatusSelectWrapper = styled.div`
  min-width: 140px;
  font-size: 12px;
`;

const PAGE_SIZE = 20;

const FileUploadZone = styled.div`
  margin-top: 8px;
  padding: 16px;
  border: 2px dashed
    ${(props) =>
      props.$isDragOver
        ? themeGet("colors.primary")
        : themeGet("colors.greyLight")};
  border-radius: 8px;
  background: ${(props) =>
    props.$isDragOver
      ? themeGet("colors.primaryLightest")
      : themeGet("colors.greyLightest")};
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${themeGet("colors.primary")};
    background: ${themeGet("colors.primaryLightest")};
  }
`;

const UploadIcon = styled.div`
  font-size: 24px;
  color: ${themeGet("colors.primary")};
  margin-bottom: 8px;
`;

const UploadText = styled.div`
  font-size: 13px;
  color: ${themeGet("colors.greyDark")};
`;

const UploadHint = styled.div`
  font-size: 11px;
  color: ${themeGet("colors.grey")};
  margin-top: 4px;
`;

const UploadProgress = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${themeGet("colors.primary")};
`;

const HiddenInput = styled.input`
  display: none;
`;

const getStatusVariant = (status) => {
  switch (status?.toLowerCase()) {
    case "completed":
      return "success";
    case "running":
      return "primary";
    case "failed":
      return "danger";
    case "paused":
    case "paused_for_approval":
    case "paused_for_upload":
    case "waiting_approval":
    case "waiting_upload":
      return "warning";
    case "cancelled":
      return "default";
    default:
      return "default";
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case "completed":
      return "check";
    case "running":
      return "spinner";
    case "failed":
      return "times";
    case "paused":
    case "paused_for_approval":
      return "pause";
    case "waiting_approval":
      return "user-check";
    case "waiting_upload":
    case "paused_for_upload":
      return "upload";
    default:
      return "circle";
  }
};

const formatDate = (dateString) => {
  if (!dateString) {
    return "N/A";
  }
  return new Date(dateString).toLocaleString("en-AU", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (start, end) => {
  if (!start) {
    return "N/A";
  }
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  const diffMs = endDate - startDate;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) {
    return `${diffSec}s`;
  }
  if (diffSec < 3600) {
    return `${Math.floor(diffSec / 60)}m ${diffSec % 60}s`;
  }
  return `${Math.floor(diffSec / 3600)}h ${Math.floor((diffSec % 3600) / 60)}m`;
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "RUNNING", label: "Running" },
  { value: "PAUSED_FOR_APPROVAL", label: "Paused for Approval" },
  { value: "PAUSED_FOR_UPLOAD", label: "Paused for Upload" },
  { value: "COMPLETED", label: "Completed" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELLED", label: "Cancelled" },
];

function WorkflowRunPanel({ workflowId, onRunStatusChange }) {
  const { getTokenSilently } = useAuth0();
  const { apiUrl } = useApolloSettings();
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [uploadingStepId, setUploadingStepId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragOverStepId, setDragOverStepId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const handleCopyRunId = useCallback((e, runId) => {
    e.stopPropagation();
    navigator.clipboard.writeText(runId);
    setCopiedId(runId);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  // Build query variables
  const queryVariables = useMemo(
    () => ({
      workflowId,
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    [workflowId, statusFilter, currentPage]
  );

  // Fetch runs
  const {
    data: runsData,
    loading: loadingRuns,
    refetch: refetchRuns,
  } = useQuery(GetWorkflowRuns, {
    variables: queryVariables,
    skip: !workflowId,
    pollInterval: 5000, // Poll every 5 seconds for updates
  });

  const totalCount = runsData?.getWorkflowRuns?.totalCount || 0;
  const hasMore = runsData?.getWorkflowRuns?.hasMore || false;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Reset to first page when filter changes
  const handleStatusFilterChange = useCallback((option) => {
    setStatusFilter(option?.value || "");
    setCurrentPage(0);
    setSelectedRunId(null);
  }, []);

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
    setSelectedRunId(null);
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
    setSelectedRunId(null);
  }, []);

  const handleGoToPage = useCallback((page) => {
    setCurrentPage(page);
    setSelectedRunId(null);
  }, []);

  // Fetch selected run details - pause polling during upload to prevent re-renders
  const { data: runData } = useQuery(GetWorkflowRun, {
    variables: { id: selectedRunId },
    skip: !selectedRunId,
    pollInterval: uploadingStepId ? 0 : 3000, // Pause polling during upload
  });

  // Mutations
  const [triggerWorkflow, { loading: triggering }] =
    useMutation(TriggerWorkflow);
  const [cancelRun, { loading: cancelling }] = useMutation(CancelWorkflowRun);
  const [approveStep, { loading: approving }] =
    useMutation(ApproveWorkflowStep);
  const [rejectStep, { loading: rejecting }] = useMutation(RejectWorkflowStep);
  const [completeUpload] = useMutation(CompleteFileUpload);

  const runs = runsData?.getWorkflowRuns?.items || [];
  const selectedRun = runData?.getWorkflowRun;

  const handleTrigger = useCallback(async () => {
    try {
      setError(null);
      await triggerWorkflow({ variables: { input: { workflowId } } });
      // Reset to first page and clear filter to show the new run
      setCurrentPage(0);
      setStatusFilter("");
      refetchRuns();
      onRunStatusChange?.();
    } catch (err) {
      setError(err.message || "Failed to trigger workflow");
    }
  }, [workflowId, triggerWorkflow, refetchRuns, onRunStatusChange]);

  const handleCancel = useCallback(
    async (runId) => {
      try {
        setError(null);
        await cancelRun({ variables: { id: runId } });
        refetchRuns();
        onRunStatusChange?.();
      } catch (err) {
        setError(err.message || "Failed to cancel run");
      }
    },
    [cancelRun, refetchRuns, onRunStatusChange]
  );

  const handleApprove = useCallback(
    async (stepRunId) => {
      try {
        setError(null);
        await approveStep({ variables: { input: { stepRunId } } });
        refetchRuns();
        onRunStatusChange?.();
      } catch (err) {
        setError(err.message || "Failed to approve step");
      }
    },
    [approveStep, refetchRuns, onRunStatusChange]
  );

  const handleReject = useCallback(
    async (stepRunId) => {
      try {
        setError(null);
        await rejectStep({ variables: { input: { stepRunId } } });
        refetchRuns();
        onRunStatusChange?.();
      } catch (err) {
        setError(err.message || "Failed to reject step");
      }
    },
    [rejectStep, refetchRuns, onRunStatusChange]
  );

  // Handle file upload for file-upload-gate steps
  const handleFileUpload = useCallback(
    async (stepRunId, file, uploadConfig) => {
      try {
        setError(null);
        setUploadingStepId(stepRunId);
        setUploadProgress("Preparing upload...");

        // Generate the S3 key for the file
        const baseKey =
          uploadConfig?.baseKey || `workflow-uploads/${selectedRunId}`;
        const sanitizedFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const fileKey = `${baseKey}/${sanitizedFilename}`;

        // Get the auth token
        const token = await getTokenSilently();

        // Upload to S3 using the storage service
        setUploadProgress(`Uploading ${file.name}...`);
        const uploadResult = await upload(file, fileKey, token, apiUrl);

        if (!uploadResult) {
          throw new Error("Failed to upload file to S3");
        }

        // Complete the upload step
        setUploadProgress("Finalizing...");
        await completeUpload({
          variables: {
            input: {
              stepRunId,
              fileKey,
              originalFilename: file.name,
              fileSize: file.size,
            },
          },
        });

        setUploadProgress("Complete!");
        setTimeout(() => {
          setUploadProgress(null);
          setUploadingStepId(null);
        }, 1000);
        refetchRuns();
        onRunStatusChange?.();
      } catch (err) {
        // Extract GraphQL error message if available
        const graphqlError = err?.graphQLErrors?.[0]?.message;
        const message = graphqlError || err.message || "Failed to upload file";
        setError(`Upload failed: ${message}`);
        setUploadProgress(null);
        setUploadingStepId(null);
      }
    },
    [
      selectedRunId,
      completeUpload,
      refetchRuns,
      onRunStatusChange,
      getTokenSilently,
      apiUrl,
    ]
  );

  const handleFileDrop = useCallback(
    (e, stepRunId, uploadConfig) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverStepId(null);

      const files = e.dataTransfer?.files;
      if (files?.length > 0) {
        handleFileUpload(stepRunId, files[0], uploadConfig);
      }
    },
    [handleFileUpload]
  );

  const handleFileSelect = useCallback(
    (e, stepRunId, uploadConfig) => {
      const files = e.target?.files;
      if (files?.length > 0) {
        handleFileUpload(stepRunId, files[0], uploadConfig);
      }
    },
    [handleFileUpload]
  );

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) {
        pages.push({ type: "page", value: i });
      }
    } else {
      // Always show first page
      pages.push({ type: "page", value: 0 });

      let start = Math.max(1, currentPage - 1);
      let end = Math.min(totalPages - 2, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage < 3) {
        end = Math.min(3, totalPages - 2);
      }

      // Adjust if we're near the end
      if (currentPage > totalPages - 4) {
        start = Math.max(1, totalPages - 4);
      }

      // Add ellipsis if needed before middle pages
      if (start > 1) {
        pages.push({ type: "ellipsis", value: "start" });
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push({ type: "page", value: i });
      }

      // Add ellipsis if needed after middle pages
      if (end < totalPages - 2) {
        pages.push({ type: "ellipsis", value: "end" });
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push({ type: "page", value: totalPages - 1 });
      }
    }

    return pages;
  };

  return (
    <PanelContainer>
      <PanelHeader>
        <H4 mb="0">Run History</H4>
        <Button
          variant="primary"
          small
          onClick={handleTrigger}
          disabled={triggering}
          iconLeft={["fas", triggering ? "spinner" : "play"]}
        >
          {triggering ? "Starting..." : "Run Now"}
        </Button>
      </PanelHeader>

      <FilterBar>
        <FilterLabel>Status:</FilterLabel>
        <StatusSelectWrapper>
          <Select
            inputId="status-filter"
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.find((opt) => opt.value === statusFilter)}
            onChange={handleStatusFilterChange}
            isClearable={false}
            isSearchable={false}
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: "28px",
                height: "28px",
                fontSize: "12px",
                backgroundColor: "white",
              }),
              valueContainer: (base) => ({
                ...base,
                padding: "0 8px",
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: "28px",
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: "white",
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? "#007bff"
                  : state.isFocused
                  ? "#f0f0f0"
                  : "white",
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
          />
        </StatusSelectWrapper>
        {loadingRuns && (
          <FontAwesomeIcon
            icon={["fas", "sync"]}
            spin
            style={{ color: "#6c757d", fontSize: 12 }}
          />
        )}
      </FilterBar>

      {error && (
        <Box p="r">
          <Notification
            variant="danger"
            closeable
            onClose={() => setError(null)}
          >
            {error}
          </Notification>
        </Box>
      )}

      <PanelContent>
        {loadingRuns && runs.length === 0 ? (
          <Box p="r" textAlign="center">
            <Loading />
          </Box>
        ) : runs.length === 0 ? (
          <EmptyState>
            <FontAwesomeIcon
              icon={["fas", "history"]}
              style={{ fontSize: 24, opacity: 0.3, marginBottom: 8 }}
            />
            <Small display="block">
              {statusFilter ? "No runs match this filter" : "No runs yet"}
            </Small>
          </EmptyState>
        ) : (
          runs.map((run) => (
            <RunItem
              key={run.id}
              onClick={(e) => {
                // Only toggle if clicking directly on RunItem or its header elements
                if (
                  e.target === e.currentTarget ||
                  e.target.closest("[data-run-header]")
                ) {
                  setSelectedRunId(selectedRunId === run.id ? null : run.id);
                }
              }}
            >
              <RunHeader data-run-header>
                <StatusBadge variant={getStatusVariant(run.status)}>
                  <FontAwesomeIcon
                    icon={["fas", getStatusIcon(run.status)]}
                    spin={run.status?.toLowerCase() === "running"}
                    style={{ marginRight: 4 }}
                  />
                  {run.status}
                </StatusBadge>

                {run.status?.toLowerCase() === "running" && (
                  <Button
                    variant="ghost"
                    small
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel(run.id);
                    }}
                    disabled={cancelling}
                  >
                    <FontAwesomeIcon icon={["fas", "stop"]} />
                  </Button>
                )}
              </RunHeader>

              <RunMeta data-run-header>
                <MetaItem>
                  <FontAwesomeIcon icon={["fas", "fingerprint"]} />
                  <RunIdText
                    onClick={(e) => handleCopyRunId(e, run.id)}
                    title="Click to copy Run ID"
                  >
                    {copiedId === run.id ? "Copied!" : run.id.slice(0, 8)}
                  </RunIdText>
                </MetaItem>
                <MetaItem>
                  <FontAwesomeIcon icon={["fas", "calendar"]} />
                  {formatDate(run.startedAt)}
                </MetaItem>
                <MetaItem>
                  <FontAwesomeIcon icon={["fas", "clock"]} />
                  {formatDuration(run.startedAt, run.completedAt)}
                </MetaItem>
                <MetaItem>
                  <FontAwesomeIcon icon={["fas", "bolt"]} />
                  {run.triggerType?.toLowerCase()}
                </MetaItem>
              </RunMeta>

              {/* Expanded details */}
              {selectedRunId === run.id && selectedRun && (
                <StepList
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <H5 mb="s">Steps</H5>

                  {/* Step summary counts */}
                  {selectedRun.stepRuns?.length > 0 && (
                    <StepSummary>
                      <SummaryItem $color="#28a745">
                        <FontAwesomeIcon icon={["fas", "check-circle"]} />
                        {
                          selectedRun.stepRuns.filter(
                            (s) => s.status?.toLowerCase() === "completed"
                          ).length
                        }{" "}
                        completed
                      </SummaryItem>
                      <SummaryItem $color="#dc3545">
                        <FontAwesomeIcon icon={["fas", "times-circle"]} />
                        {
                          selectedRun.stepRuns.filter(
                            (s) => s.status?.toLowerCase() === "failed"
                          ).length
                        }{" "}
                        failed
                      </SummaryItem>
                      <SummaryItem $color="#6c757d">
                        <FontAwesomeIcon icon={["fas", "hourglass-half"]} />
                        {
                          selectedRun.stepRuns.filter(
                            (s) =>
                              s.status?.toLowerCase() !== "completed" &&
                              s.status?.toLowerCase() !== "failed"
                          ).length
                        }{" "}
                        other
                      </SummaryItem>
                    </StepSummary>
                  )}

                  {selectedRun.stepRuns?.map((stepRun) => (
                    <StepItem key={stepRun.id}>
                      <StepRow>
                        <StepInfo>
                          <StepIcon $status={stepRun.status}>
                            <FontAwesomeIcon
                              icon={["fas", getStatusIcon(stepRun.status)]}
                              spin={stepRun.status?.toLowerCase() === "running"}
                            />
                          </StepIcon>
                          <StepDetails>
                            <StepName>
                              {stepRun.step?.name || "Unknown Step"}
                            </StepName>
                            <StepStatusText $status={stepRun.status}>
                              {stepRun.status}
                              {stepRun.completedAt &&
                                stepRun.startedAt &&
                                ` • ${formatDuration(
                                  stepRun.startedAt,
                                  stepRun.completedAt
                                )}`}
                            </StepStatusText>
                          </StepDetails>
                        </StepInfo>

                        {/* Show approval actions for paused steps */}
                        {(stepRun.status?.toLowerCase() === "paused" ||
                          stepRun.status?.toLowerCase() ===
                            "waiting_approval") &&
                          stepRun.approval && (
                            <ApprovalActions>
                              <ApprovalButton
                                $variant="approve"
                                onClick={() => handleApprove(stepRun.id)}
                                disabled={approving}
                              >
                                <FontAwesomeIcon icon={["fas", "check"]} />{" "}
                                Approve
                              </ApprovalButton>
                              <ApprovalButton
                                $variant="reject"
                                onClick={() => handleReject(stepRun.id)}
                                disabled={rejecting}
                              >
                                <FontAwesomeIcon icon={["fas", "times"]} />{" "}
                                Reject
                              </ApprovalButton>
                            </ApprovalActions>
                          )}
                      </StepRow>

                      {/* Show file upload zone for waiting_upload steps */}
                      {stepRun.status?.toLowerCase() === "waiting_upload" &&
                        uploadingStepId !== stepRun.id && (
                          <FileUploadZone
                            $isDragOver={dragOverStepId === stepRun.id}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDragOverStepId(stepRun.id);
                            }}
                            onDragLeave={(e) => {
                              e.stopPropagation();
                              setDragOverStepId(null);
                            }}
                            onDrop={(e) =>
                              handleFileDrop(
                                e,
                                stepRun.id,
                                stepRun.step?.config
                              )
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              document
                                .getElementById(`file-input-${stepRun.id}`)
                                ?.click();
                            }}
                          >
                            <HiddenInput
                              id={`file-input-${stepRun.id}`}
                              type="file"
                              onChange={(e) =>
                                handleFileSelect(
                                  e,
                                  stepRun.id,
                                  stepRun.step?.config
                                )
                              }
                              accept={
                                stepRun.step?.config?.allowedFileTypes?.length >
                                0
                                  ? stepRun.step.config.allowedFileTypes
                                      .map((t) => `.${t}`)
                                      .join(",")
                                  : undefined
                              }
                            />
                            <UploadIcon>
                              <FontAwesomeIcon
                                icon={["fas", "cloud-upload-alt"]}
                              />
                            </UploadIcon>
                            <UploadText>
                              {stepRun.step?.config?.message ||
                                "Drop a file here or click to select"}
                            </UploadText>
                            <UploadHint>
                              {stepRun.step?.config?.allowedFileTypes?.length >
                              0
                                ? `Allowed: ${stepRun.step.config.allowedFileTypes.join(
                                    ", "
                                  )}`
                                : "All file types accepted"}
                              {stepRun.step?.config?.maxFileSizeMB &&
                                ` • Max ${stepRun.step.config.maxFileSizeMB} MB`}
                            </UploadHint>
                          </FileUploadZone>
                        )}

                      {/* Show upload progress */}
                      {uploadingStepId === stepRun.id && (
                        <FileUploadZone $isDragOver={false}>
                          <UploadIcon>
                            <FontAwesomeIcon icon={["fas", "spinner"]} spin />
                          </UploadIcon>
                          <UploadProgress>{uploadProgress}</UploadProgress>
                        </FileUploadZone>
                      )}

                      {/* Show uploaded file info for completed upload steps */}
                      {stepRun.status?.toLowerCase() === "completed" &&
                        stepRun.step?.componentType === "file-upload-gate" &&
                        stepRun.outputDataRef && (
                          <UploadHint
                            style={{ marginTop: 8, color: "#28a745" }}
                          >
                            <FontAwesomeIcon
                              icon={["fas", "check-circle"]}
                              style={{ marginRight: 4 }}
                            />
                            File uploaded:{" "}
                            {stepRun.outputDataRef.split("/").pop()}
                          </UploadHint>
                        )}

                      {/* Show error message for failed steps */}
                      {stepRun.status?.toLowerCase() === "failed" &&
                        stepRun.errorMessage && (
                          <StepError>
                            <FontAwesomeIcon
                              icon={["fas", "exclamation-triangle"]}
                              style={{ marginRight: 6 }}
                            />
                            {stepRun.errorMessage}
                          </StepError>
                        )}
                    </StepItem>
                  ))}

                  {/* Run-level error message */}
                  {selectedRun.errorMessage && (
                    <Box mt="s" p="s" bg="dangerLightest" borderRadius="1">
                      <Small color="danger">
                        <FontAwesomeIcon icon={["fas", "exclamation-circle"]} />{" "}
                        <strong>Run Error:</strong> {selectedRun.errorMessage}
                      </Small>
                    </Box>
                  )}
                </StepList>
              )}
            </RunItem>
          ))
        )}
      </PanelContent>

      {totalCount > 0 && (
        <PaginationBar>
          <PaginationInfo>
            Showing {currentPage * PAGE_SIZE + 1}-
            {Math.min((currentPage + 1) * PAGE_SIZE, totalCount)} of{" "}
            {totalCount}
          </PaginationInfo>
          <PaginationButtons>
            <PageButton
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              title="Previous page"
            >
              <FontAwesomeIcon icon={["fas", "chevron-left"]} />
            </PageButton>

            {getPageNumbers().map((item) =>
              item.type === "ellipsis" ? (
                <span
                  key={`ellipsis-${item.value}`}
                  style={{ padding: "0 4px", color: "#6c757d" }}
                >
                  ...
                </span>
              ) : (
                <PageButton
                  key={`page-${item.value}`}
                  $active={currentPage === item.value}
                  onClick={() => handleGoToPage(item.value)}
                >
                  {item.value + 1}
                </PageButton>
              )
            )}

            <PageButton
              onClick={handleNextPage}
              disabled={!hasMore}
              title="Next page"
            >
              <FontAwesomeIcon icon={["fas", "chevron-right"]} />
            </PageButton>
          </PaginationButtons>
        </PaginationBar>
      )}
    </PanelContainer>
  );
}

WorkflowRunPanel.propTypes = {
  workflowId: PropTypes.string.isRequired,
  onRunStatusChange: PropTypes.func,
};

export default memo(WorkflowRunPanel);
